// Shared wager-slip state + actions, lifted verbatim from the sportsbook lobby
// (pages/index.vue) so the scoreboard's odds widgets behave EXACTLY like the
// book: same selection model, same stake math, same place-bet payload/flow.
// Each caller gets its own instance (plain refs) — a page only mounts one
// OddsBoard, and the board + slip live in that single component, so they share
// this one instance.
export interface SlipOutcome { name: string; price: number; point?: number | null }
export interface SlipGame {
  event_id: string; sport: string; home_team: string; away_team: string
  commence_time: string
  markets: { h2h: SlipOutcome[]; spreads: SlipOutcome[]; totals: SlipOutcome[] }
}
export interface SlipFuture {
  event_id: string; sport: string; title: string; commence_time: string
  outcomes: SlipOutcome[]
}
interface Sel {
  ev: { event_id: string; sport: string; home_team: string; away_team: string; commence_time: string }
  market: string; pick: string; line: number | null; odds: number; label: string
}

export function useBetSlip() {
  const api = useApi()
  const toast = useToast()
  const { user, refreshBalance } = useAuth()
  const playSite = usePlaySite()
  const venueLookup = useVenueLookup()

  const sel = ref<Sel | null>(null)
  const stake = ref<number | null>(null)
  const placing = ref(false)
  // client idempotency token for the pending submit: reset whenever the selection
  // or stake changes (a different logical bet), minted at submit, cleared on success
  // (clearSel) — so a double-click / network-retry of the SAME slip reuses one token
  // and the backend creates exactly one charge + one bet.
  const txId = ref('')
  watch([sel, stake], () => { txId.value = '' })

  function selH2h(g: SlipGame, o: SlipOutcome) {
    sel.value = { ev: g, market: 'h2h', pick: o.name, line: null, odds: o.price, label: o.name }
  }
  function selSpread(g: SlipGame, o: SlipOutcome) {
    const pt = o.point != null ? (o.point > 0 ? `+${o.point}` : `${o.point}`) : ''
    sel.value = { ev: g, market: 'spreads', pick: o.name, line: o.point ?? null, odds: o.price, label: `${o.name} ${pt}` }
  }
  function selTotal(g: SlipGame, o: SlipOutcome) {
    sel.value = { ev: g, market: 'totals', pick: o.name, line: o.point ?? null, odds: o.price, label: `${o.name} ${o.point ?? ''}` }
  }
  function selFuture(f: SlipFuture, o: SlipOutcome) {
    sel.value = { ev: { event_id: f.event_id, sport: f.sport, home_team: f.title, away_team: '', commence_time: f.commence_time }, market: 'outrights', pick: o.name, line: null, odds: o.price, label: `${o.name} (outright)` }
  }
  function isSel(eid: string, market: string, pick: string) {
    return sel.value?.ev.event_id === eid && sel.value?.market === market && sel.value?.pick === pick
  }
  function clearSel() { sel.value = null; stake.value = null }

  const potential = computed(() => sel.value && stake.value ? +(stake.value * sel.value.odds).toFixed(2) : 0)
  const toWin = computed(() => sel.value && stake.value ? +(potential.value - stake.value).toFixed(2) : 0)
  const balance = computed(() => user.value?.balance ?? null)
  // signed-out users can browse odds on the public scoreboard but must sign in
  // to wager; the slip surfaces that and the button routes to /login.
  const signedIn = computed(() => !!user.value)
  const canPlace = computed(() =>
    !!user.value && !!sel.value && !!stake.value && stake.value >= 1 && !placing.value &&
    (balance.value === null || stake.value <= balance.value))

  // --- where are you playing? (BMO venue picker) -------------------------------
  // The picker sits between the place-bet tap and the request. A choice (venue
  // or home) is remembered for the pass TTL (usePlaySite), so only the first bet
  // in a window opens it; the slip's "playing at … · change" line reopens it
  // without placing anything.
  const pickingVenue = ref(false)
  // bumped per open so every open is a FRESH picker instance: its auto-select
  // fires at most once per instance, and a server-rejected venue gets a clean prompt
  const pickerKey = ref(0)
  const playingAt = playSite.playingAt
  let placePending = false    // the picker is open FOR a submit (false for "change")
  let pendingPlaced: (() => void | Promise<void>) | undefined

  function openPicker(forPlace: boolean, onPlaced?: () => void | Promise<void>) {
    placePending = forPlace
    pendingPlaced = forPlace ? onPlaced : undefined
    pickerKey.value += 1
    pickingVenue.value = true
  }

  // onPlaced lets the host refresh anything it shows (e.g. a recent-bets list).
  async function placeBet(onPlaced?: () => void | Promise<void>) {
    if (!user.value) { await navigateTo('/login'); return }
    if (!sel.value || !stake.value) return
    // mint once, BEFORE the picker: the submit resumed after the pick (or a
    // retry / double-click) reuses this token, so the backend charges exactly once
    if (!txId.value) txId.value = newUlid()
    const cur = playSite.current()
    if (!cur) { openPicker(true, onPlaced); return }
    if (await submit(cur.siteId, cur.name, onPlaced)) openPicker(true, onPlaced)
  }

  // the picker's answer — a tapped row or its auto-select. remember it, then
  // place the pending bet (if the picker was opened for one).
  async function confirmVenue(siteId: number, name: string) {
    playSite.set(siteId, name)
    if (!placePending) { pickingVenue.value = false; return }   // "change": nothing to place
    placePending = false
    const onPlaced = pendingPlaced
    pendingPlaced = undefined
    // hold the card ~1s so an auto-selected "playing at … ✓" registers while the bet posts
    const [rejected] = await Promise.all([
      submit(siteId, name, onPlaced),
      new Promise<void>((r) => setTimeout(r, 900)),
    ])
    if (rejected) openPicker(true, onPlaced)
    else pickingVenue.value = false
  }

  function cancelVenue() {
    placePending = false
    pendingPlaced = undefined
    pickingVenue.value = false
  }
  function changeVenue() { openPicker(false) }

  // POST the slip with the chosen site. Returns true when the server rejected
  // that venue (venue_not_verified) — the caller reopens the picker; the
  // remembered site is already cleared here.
  async function submit(siteId: number, siteName: string, onPlaced?: () => void | Promise<void>): Promise<boolean> {
    if (!sel.value || !stake.value) return false
    // normally minted in placeBet; re-mint only if the slip changed while the picker
    // was open (the sel/stake watch cleared it) so the POST never carries an empty token
    if (!txId.value) txId.value = newUlid()
    placing.value = true
    const label = sel.value.label
    try {
      await api.post('/bets', {
        sport: sel.value.ev.sport, event_id: sel.value.ev.event_id,
        home_team: sel.value.ev.home_team, away_team: sel.value.ev.away_team,
        market: sel.value.market, pick: sel.value.pick, line: sel.value.line,
        odds: sel.value.odds, stake: Math.round(stake.value),
        commence_time: sel.value.ev.commence_time,
        transaction_id: txId.value,
        site_id: siteId,   // 1 = home; >1 must match the player's fresh venue lookup
      })
      clearSel()   // resets txId via the watch -> the next bet gets a fresh token
      await refreshBalance()
      if (onPlaced) await onPlaced()
      toast.ok(`bet placed: ${label}` + (siteId > 1 ? ` · playing at ${siteName}` : ''))
    } catch (e: any) {
      const detail = e?.data?.detail
      const code = typeof detail === 'string' ? detail : detail?.code
      if (code === 'venue_not_verified') {
        // the venue isn't backed by a fresh lookup server-side (expired, or
        // another device's pass) — forget it and ask again. same txId, so the
        // resubmit is idempotent.
        playSite.clear()
        venueLookup.reset()
        toast.error('that venue needs a fresh location check — choose again')
        return true
      } else if (code === 'self_excluded') {
        // compliance stop — self-exclusion closes ALL play, BMO included (IR-6)
        toast.error('You’re self-excluded — betting is closed. You can still withdraw.')
      } else if (code === 'odds_changed') {
        // the book re-prices every wager server-side; the quote on screen went
        // stale, so show the live price and let them place it again deliberately
        if (sel.value && typeof detail?.odds === 'number') {
          sel.value = { ...sel.value, odds: detail.odds, line: detail.line ?? sel.value.line }
        }
        toast.error('the price moved — check the new odds and place again')
      } else if (code === 'odds_unavailable') {
        toast.error('odds are temporarily unavailable — try again shortly')
      } else if (code === 'event_not_offered' || code === 'selection_not_offered'
                 || code === 'selection_not_priced') {
        toast.error('that selection is no longer offered')
      } else {
        toast.error(typeof detail === 'string' ? detail : 'could not place bet')
      }
    } finally { placing.value = false }
    return false
  }

  return {
    sel, stake, placing,
    selH2h, selSpread, selTotal, selFuture, isSel, clearSel,
    potential, toWin, balance, signedIn, canPlace, placeBet,
    pickingVenue, pickerKey, playingAt, confirmVenue, cancelVenue, changeVenue,
  }
}
